import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiPlus, FiEdit2, FiTrash2, FiAlertTriangle,
    FiSearch, FiDollarSign, FiRefreshCw,
} from "react-icons/fi";
import salaryMasterService from "../../services/salaryMasterService";
import ConfirmModal from "../../components/common/ConfirmModal";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const CATEGORY_OPTIONS = ["Teaching", "Non Teaching"];

const normalizeSalaryMasterRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

export default function SalaryMasterList() {
    const navigate = useNavigate();

    const [data, setData] = useState([]); // [{ employee, master }]
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [catFilter, setCatFilter] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await salaryMasterService.getAll({
                search,
                employeeCategory: catFilter,
            });
            setData(normalizeSalaryMasterRows(res));
        } catch (err) {
            setData([]);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [search, catFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await salaryMasterService.remove(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            fetchData();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    const rows = (Array.isArray(data) ? data : []).filter((d) => d?.employee);
    const noMasterCount = rows.filter((d) => !d.master).length;

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">Salary Structures</h1>
                    <p className="page-subtitle">
                        {rows.length} employee{rows.length !== 1 ? "s" : ""}
                        {noMasterCount > 0 && (
                            <span style={{ color: "#dc2626", marginLeft: 8, fontWeight: 600 }}>
                                · {noMasterCount} with no structure set
                            </span>
                        )}
                    </p>
                </div>
                <button className="btn btn-primary" onClick={() => navigate("/salary-master/new")}>
                    <FiPlus size={14} /> Add Structure
                </button>
            </div>

            {/* ── No structure warning ── */}
            {noMasterCount > 0 && (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiAlertTriangle size={14} />
                    <strong>{noMasterCount} employee{noMasterCount !== 1 ? "s" : ""}</strong> {noMasterCount !== 1 ? "have" : "has"} no salary structure. Payroll generation will skip them.
                    <button className="btn btn-outline btn-sm" style={{ marginLeft: "auto" }} onClick={() => navigate("/salary-master/new")}>
                        <FiPlus size={12} /> Add Now
                    </button>
                </div>
            )}

            <div className="card">
                {/* ── Filters ── */}
                <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
                    <div className="search-bar">
                        <FiSearch size={14} color="#9ca3af" />
                        <input
                            placeholder="Search by name or employee code..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select className="form-control" style={{ width: 170, fontSize: 13 }} value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
                        <option value="">All Categories</option>
                        {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button className="btn btn-outline btn-sm" onClick={fetchData} style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <FiRefreshCw size={12} /> Refresh
                    </button>
                </div>

                {/* ── Table ── */}
                {loading ? (
                    <div className="loading-center"><div className="spinner" />Loading salary structures...</div>
                ) : rows.length === 0 ? (
                    <div className="empty-state">
                        <FiDollarSign size={48} style={{ opacity: 0.25, display: "block", margin: "0 auto 0.75rem" }} />
                        <p>No employees found.</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Employee</th>
                                    <th>Category</th>
                                    <th>Effective From</th>
                                    <th style={{ textAlign: "right" }}>Basic</th>
                                    <th style={{ textAlign: "right" }}>DA</th>
                                    <th style={{ textAlign: "right" }}>HRA</th>
                                    <th style={{ textAlign: "right" }}>Gross</th>
                                    <th style={{ textAlign: "right" }}>Deductions</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(({ employee, master }, i) => {
                                    const totalDeductions = master
                                        ? (master.pf + master.pt + master.tds + master.otherDeductions)
                                        : 0;
                                    return (
                                        <tr key={employee._id}>
                                            <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{i + 1}</td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
                                                    <div style={{ width: 36, height: 36, borderRadius: 10, overflow: "hidden", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#475569", flexShrink: 0 }}>
                                                        {employee.photo?.url
                                                            ? <img src={employee.photo.url} alt={employee.fullName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                            : employee.fullName.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("")}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: 13 }}>{employee.fullName}</div>
                                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                            {employee.employeeCode} · {employee.designation}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ fontSize: 13 }}>{employee.employeeCategory}</td>

                                            {master ? (
                                                <>
                                                    <td>
                                                        <span className="badge badge-blue">{master.effectiveFrom}</span>
                                                    </td>
                                                    <td style={{ textAlign: "right", fontSize: 13 }}>{inr(master.basicPay)}</td>
                                                    <td style={{ textAlign: "right", fontSize: 13 }}>{inr(master.da)}</td>
                                                    <td style={{ textAlign: "right", fontSize: 13 }}>{inr(master.hra)}</td>
                                                    <td style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: "#d97706" }}>{inr(master.grossPay)}</td>
                                                    <td style={{ textAlign: "right", fontSize: 13, color: "#dc2626" }}>{inr(totalDeductions)}</td>
                                                    <td>
                                                        <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                                                            <button
                                                                className="btn btn-outline btn-sm"
                                                                title="Add salary revision"
                                                                onClick={() => navigate(`/salary-master/new?employeeId=${employee._id}`)}
                                                            >
                                                                <FiPlus size={12} /> Revise
                                                            </button>
                                                            <button
                                                                className="btn btn-outline btn-sm"
                                                                onClick={() => navigate(`/salary-master/${master._id}/edit`)}
                                                            >
                                                                <FiEdit2 size={12} />
                                                            </button>
                                                            <button
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => setDeleteTarget(master)}
                                                            >
                                                                <FiTrash2 size={12} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td colSpan={6}>
                                                        <span style={{ fontSize: 12.5, color: "#dc2626", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                                                            <FiAlertTriangle size={12} /> No salary structure set
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            onClick={() => navigate(`/salary-master/new?employeeId=${employee._id}`)}
                                                        >
                                                            <FiPlus size={12} /> Set Up
                                                        </button>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Salary Structure"
                    message={`Delete this salary structure (effective ${deleteTarget.effectiveFrom})? This cannot be undone.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
